/**
 * Products adapter — maps the DyntraStore NestJS /v1/products shape into the
 * flat product shape that the Emergent UI components expect (see ProductCard,
 * ProductDetail, BulkBuy, Refurbished, OffersOfDay).
 *
 * The NestJS catalogue seed is currently smaller than the old-UI catalogue
 * (only ~1500 SKUs vs ~4700 image-derived rows). To keep the frontend feeling
 * "as full as before" we also read `product-image-manifest.json` from the
 * public folder for:
 *   - image URL resolution when the API returns image: null
 *   - PLP augmentation when the API returns fewer than requested SKUs
 */
import { api } from "@/lib/api";
import {
  loadImageManifest,
  resolveProductImage,
  getImageProducts,
  getImageProductBySlug,
} from "@/lib/manifest";

const BULK_PACK_PRESETS = [5, 10, 20, 50];

function pickImage(p) {
  if (!p) return null;
  if (typeof p.image === "string" && p.image) return p.image;
  const media = Array.isArray(p.media) ? p.media : Array.isArray(p.images) ? p.images : Array.isArray(p.gallery) ? p.gallery : [];
  const first = media[0];
  if (!first) return null;
  return typeof first === "string" ? first : first.url || null;
}

function pickImages(p) {
  const media = Array.isArray(p?.media)
    ? p.media
    : Array.isArray(p?.gallery)
      ? p.gallery
      : Array.isArray(p?.images)
        ? p.images
        : [];
  const urls = media
    .map((m) => (typeof m === "string" ? m : m?.url))
    .filter(Boolean);
  return urls.length ? urls : [pickImage(p)].filter(Boolean);
}

function priceLike(p) {
  const price = Number(p?.price ?? 0);
  const mrp = Number(p?.mrp ?? p?.originalPrice ?? p?.compareAtPrice ?? price);
  const discount =
    mrp > price && mrp > 0
      ? Math.round(((mrp - price) / mrp) * 100)
      : Number(p?.discount ?? 0);
  return { price, mrp, discount };
}

function specsToArray(specs) {
  if (!specs) return [];
  if (Array.isArray(specs)) return specs;
  if (typeof specs === "object") return Object.entries(specs).map(([k, v]) => `${k}: ${v}`);
  return [];
}

/** Turn any NestJS product-ish object into the flat shape the UI wants. */
export function normalizeProduct(raw) {
  if (!raw) return null;
  const { price, mrp, discount } = priceLike(raw);
  const stock = Number(raw.stock ?? 0);

  // Try to resolve the image URL from the manifest when the API returns null
  // or a stock CDN placeholder (unsplash/pexels are reused across many SKUs
  // in the seed data and cause visible duplicates on PLPs).
  const rawImage = pickImage(raw);
  const brandName = raw.brand?.name || raw.brandName || raw.brand;
  const brandId = raw.brand?.slug || raw.brand?.id || raw.brandId;
  const slug = raw.slug || raw.id;
  // Stock CDN URLs (Unsplash/Pexels) are reused across many SKUs in seed data
  // and cause visible duplicates on PLPs. We never surface them — the UI's
  // <img onError> handler falls back to a local placeholder SVG when null.
  const isStockUrl = typeof rawImage === "string" && /(?:images\.unsplash\.com|images\.pexels\.com|placehold\.co|placeholder)/i.test(rawImage);
  const manifestImage = resolveProductImage({
    slug,
    id: raw.id,
    productId: raw.productId || raw.id,
    name: raw.name || raw.title,
    brand: brandName,
  });
  const resolved = manifestImage || (isStockUrl ? null : rawImage) || null;
  const rawImages = pickImages(raw).filter(
    (u) => u && !/(?:images\.unsplash\.com|images\.pexels\.com|placehold\.co|placeholder)/i.test(u)
  );
  const finalImages = manifestImage
    ? [manifestImage, ...rawImages.filter((u) => u !== manifestImage)]
    : rawImages;

  return {
    // NestJS `/v1/products/:slug` only resolves by slug, so we surface the slug
    // as the primary UI identifier (URLs, cart keys) and keep the DB id around
    // in `productId` for anything that needs it.
    id: slug,
    productId: raw.id || slug,
    slug,
    name: raw.name || raw.title || "Untitled",
    price,
    mrp,
    discount,
    image: resolved,
    images: finalImages,
    rating: Number(raw.rating ?? 4.5),
    reviews: Number(raw.reviewCount ?? raw.reviews ?? 0),
    in_stock: stock > 0 || raw.inStock === true,
    stock,
    badge: raw.badge || (discount >= 15 ? `Save ${discount}%` : null),
    badge_type: raw.badge_type || (discount >= 15 ? "discount" : null),
    category:
      raw.category?.slug ||
      (Array.isArray(raw.categories) && raw.categories[0]?.slug) ||
      raw.categorySlug ||
      null,
    brand: brandName || null,
    brand_slug: brandId || null,
    description:
      raw.description ||
      raw.shortDescription ||
      raw.summary ||
      "",
    features: Array.isArray(raw.features)
      ? raw.features
      : Array.isArray(raw.highlights)
        ? raw.highlights
        : specsToArray(raw.specifications).slice(0, 4),
    specifications: raw.specifications || raw.specs || null,
    rewards: Number(raw.rewards ?? 0),
  };
}

/** Turn a manifest imageProduct row into the flat UI shape. */
function normalizeImageProduct(p) {
  const price = Number(p?.price ?? 0);
  const mrp = Number(p?.mrp ?? price);
  const discount = mrp > price && mrp > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const first = Array.isArray(p?.gallery) && p.gallery.length ? p.gallery[0] : p.image;
  const stock = Number(p?.stock ?? 25);
  return {
    id: p.slug || p.id,
    productId: p.id,
    slug: p.slug || p.id,
    name: p.name,
    price,
    mrp,
    discount,
    image: first || null,
    images: Array.isArray(p?.gallery) ? p.gallery : first ? [first] : [],
    rating: Number(p?.rating ?? 4.4),
    reviews: Number(p?.reviewCount ?? p?.reviews ?? 0),
    in_stock: stock > 0,
    stock,
    badge: discount >= 15 ? `Save ${discount}%` : null,
    badge_type: discount >= 15 ? "discount" : null,
    category: p.primaryCategorySlug || p.categorySlug || (Array.isArray(p.categorySlugs) ? p.categorySlugs[0] : null),
    brand: p.brand || p.brandName || null,
    brand_slug: p.brandId || null,
    description: "",
    features: [],
    specifications: null,
    rewards: 0,
    __source: "manifest",
  };
}

function decorateBulk(p, idx) {
  const pack_size = BULK_PACK_PRESETS[idx % BULK_PACK_PRESETS.length];
  const perUnitSavings = Math.max(0, p.mrp - p.price);
  const savings = Math.round(perUnitSavings * pack_size + p.price * (0.05 + idx * 0.01) * pack_size);
  const extra_off = 5 + Math.min(20, idx * 3);
  return { ...p, pack_size, savings, extra_off };
}

/**
 * List products.
 *
 * The Emergent UI uses `type=bulk|refurbished|offer` for the promo strips.
 * NestJS `/v1/products` doesn't have a `type` param, so we translate:
 *   - bulk        → top-rated / high-stock heuristic, then decorate with pack_size/savings
 *   - refurbished → q=refurbished (best-effort text match)
 *   - offer       → discountMin=10 (deep discounts)
 */
export async function listProducts({ type, limit = 20, category, brand, q } = {}) {
  const params = { pageSize: limit };
  if (category) params.category = category;
  if (brand) params.brand = brand;
  if (q) params.q = q;

  if (type === "offer") params.discountMin = 10;
  if (type === "refurbished") params.sort = "rating_desc";
  if (type === "bulk") params.sort = "popular";

  // Kick off manifest load in parallel with the API call.
  const manifestReady = loadImageManifest();

  let apiItems = [];
  let apiTotal = 0;
  try {
    const { data } = await api.get("/products", { params });
    const rawItems = Array.isArray(data) ? data : data.items || [];
    apiItems = rawItems;
    apiTotal = data?.total ?? rawItems.length;
  } catch (err) {
    console.warn("[products] api list failed", err?.message);
  }

  // Ensure the manifest is available before normalising (so image resolution runs).
  await manifestReady;

  // Drop API products whose image couldn't be resolved locally — they only
  // add visual noise (many placeholders in a row look like duplicates). The
  // manifest augmentation below always has a real image, so PLPs stay full.
  const normalised = apiItems.map(normalizeProduct).filter((p) => p && p.image);
  const seen = new Set(normalised.map((p) => p.id));

  // Augment with image-manifest products so PLPs never look sparse.
  const augment = getImageProducts({ categorySlug: category, brandId: brand, q, limit: limit * 3 });
  const extras = [];
  for (const raw of augment) {
    const p = normalizeImageProduct(raw);
    if (p && p.id && !seen.has(p.id)) {
      extras.push(p);
      seen.add(p.id);
    }
    if (normalised.length + extras.length >= limit) break;
  }

  const combined = [...normalised, ...extras];
  const finalItems = type === "bulk" ? combined.map(decorateBulk) : combined;
  const total = Math.max(apiTotal, finalItems.length);
  return {
    items: finalItems,
    total,
  };
}

/** GET /v1/products/:idOrSlug — NestJS treats the param as a slug. */
export async function getProduct(idOrSlug) {
  if (!idOrSlug) return null;
  await loadImageManifest();
  try {
    const { data } = await api.get(`/products/${encodeURIComponent(idOrSlug)}`);
    if (data) return normalizeProduct(data);
  } catch {
    /* fall through to manifest lookup */
  }
  const alt = getImageProductBySlug(idOrSlug);
  return alt ? normalizeImageProduct(alt) : null;
}
